/* eslint-disable react-native/no-raw-text */
/**
 * CommunicationPatternInsight — json-render web wrapper
 *
 * Web implementation for the json-render catalog (web-only).
 * Pattern shift detection with confidence and contributing signals.
 * Maps to: analysis-results/AiDetectionSection.tsx
 * CRITICAL: Never use "AI" in description. Use "Communication Pattern".
 *
 * Task: NocoDB #2084
 * Agent: Diego <diego@liftping.net>
 */
"use client";

import React, { useMemo } from "react";
import { useTranslation } from "@dramadetector/shared";
import { cn } from "../../lib/utils";
import type { CommunicationPatternInsightProps } from "../schema";

const labelColors: Record<
	"natural" | "mixed" | "likely_assisted",
	{ color: string; bgColor: string }
> = {
	natural: { color: "text-green-400", bgColor: "bg-green-400" },
	mixed: { color: "text-amber-400", bgColor: "bg-amber-400" },
	likely_assisted: { color: "text-amber-500", bgColor: "bg-amber-500" },
};

export function CommunicationPatternInsight({
	aiLikelihood,
	label,
	hasBaseline,
	signals = [],
}: CommunicationPatternInsightProps) {
	const { t } = useTranslation("analysis") as {
		t: (key: string, options?: any) => string;
	};

	const colors = labelColors[label];
	const sortedSignals = useMemo(
		() => [...signals].sort((a, b) => b.weight * b.score - a.weight * a.score),
		[signals],
	);

	if (!hasBaseline) return null;

	return (
		<div className="backdrop-blur-sm bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
			<div className="flex items-center justify-between">
				<h4 className="text-sm font-semibold text-[#F3F4F6]">
					{t("ai_detection.header")}
				</h4>
				<span className={cn("text-xs font-medium", colors.color)}>
					{t(`ai_detection.label.${label}`)}
				</span>
			</div>
			<div className="space-y-1">
				<div className="flex justify-between text-xs text-[#9CA3AF]">
					<span>{t("ai_detection.pattern_confidence")}</span>
					<span>
						{Math.round(aiLikelihood * 100)}
						{"%"}
					</span>
				</div>
				<div className="h-2 bg-white/10 rounded-full overflow-hidden">
					<div
						className={cn("h-full rounded-full", colors.bgColor)}
						style={{ width: `${aiLikelihood * 100}%` }}
					/>
				</div>
			</div>
			{sortedSignals.length > 0 && (
				<div className="space-y-1.5">
					<span className="text-xs text-[#9CA3AF]">
						{t("ai_detection.contributing_signals")}
					</span>
					{sortedSignals.map((signal) => (
						<div
							key={signal.name}
							className="flex items-center justify-between text-xs"
						>
							<span className="text-[#D1D5DB]">{signal.name}</span>
							<span className="text-[#9CA3AF]">
								{Math.round(signal.score * 100)}
								{"%"}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
